﻿import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { StringMirrorObject } from "../../objects/StringMirrorObject";
import { StringObject } from "../../objects/StringObject";
import { EasingFunctionOutput } from "../../tools/EasingFunctionTool";
import { styleTxn } from "../../utils/style-txn";

const SIDE_UNKNOWN = -1;
const SIDE_BEFORE = 0;
const SIDE_AFTER = 1;
const SIDE_INSIDE = 2;

export const STRING_DEV_PROGRESS_OVERRIDE_KEY = "__string-dev-progress-override";

type ProgressComputeKernel = {
  computeRawProgress(scroll: number, start: number, difference: number): number;
  computeRawProgressBatch(
    scroll: number,
    starts: Float64Array,
    differences: Float64Array,
    out: Float64Array,
    length: number,
  ): void;
};

const DEFAULT_PROGRESS_KERNEL: ProgressComputeKernel = {
  computeRawProgress(scroll: number, start: number, difference: number): number {
    if (difference === 0) return 0;
    const raw = (scroll - start) / difference;
    if (raw <= 0) return 0;
    if (raw >= 1) return 1;
    return raw;
  },
  computeRawProgressBatch(
    scroll: number,
    starts: Float64Array,
    differences: Float64Array,
    out: Float64Array,
    length: number,
  ): void {
    for (let i = 0; i < length; i++) {
      const diff = differences[i];
      if (diff === 0) {
        out[i] = 0;
        continue;
      }
      const raw = (scroll - starts[i]) / diff;
      if (raw <= 0) out[i] = 0;
      else if (raw >= 1) out[i] = 1;
      else out[i] = raw;
    }
  },
};

export class StringProgress extends StringModule {
  protected updateScheduled = false;
  private batchStarts: Float64Array = new Float64Array(0);
  private batchDiffs: Float64Array = new Float64Array(0);
  private batchOut: Float64Array = new Float64Array(0);

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "progress";

    this.cssProperties = [
      { name: "--progress", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--progress-slice", syntax: "<number>", initialValue: "0", inherits: true },
    ];

    this.attributesToMap = [
      ...this.attributesToMap,
      { key: "easing", type: "easing", fallback: this.settings["easing"] },
      { key: "precision", type: "number", fallback: this.settings["precision"] ?? -1 },
    ];
  }

  initializeObject(
    globalId: number,
    object: StringObject,
    element: HTMLElement,
    attributes: Record<string, any>,
  ): void {
    super.initializeObject(globalId, object, element, attributes);
  }

  private sanitizeRawProgress(rawProgress: number): number {
    if (!Number.isFinite(rawProgress)) return 0;
    if (rawProgress <= 0) return 0;
    if (rawProgress >= 1) return 1;
    const EPS = 1e-4;
    if (rawProgress > 1 - EPS) rawProgress = 1;
    else if (rawProgress < EPS) rawProgress = 0;
    return rawProgress;
  }

  private resolveRawProgress(
    object: StringObject,
    scroll: number,
    startPosition: number,
    differencePosition: number,
  ): number {
    const overrideRawProgress = object.getProperty<number | null>(STRING_DEV_PROGRESS_OVERRIDE_KEY);
    if (overrideRawProgress != null) {
      return this.sanitizeRawProgress(overrideRawProgress);
    }

    return DEFAULT_PROGRESS_KERNEL.computeRawProgress(scroll, startPosition, differencePosition);
  }

  private applyRawProgress(object: StringObject, rawProgress: number): void {
    const normalizedRaw = this.sanitizeRawProgress(rawProgress);

    const easing = object.getProperty<EasingFunctionOutput>("easing");
    const easedProgress = typeof easing === "function" ? easing(normalizedRaw) : normalizedRaw;

    const precision = object.getProperty<number>("precision") ?? -1;
    const progress =
      precision >= 0
        ? Math.round(easedProgress * 10 ** precision) / 10 ** precision
        : easedProgress;

    const prevProgress = object.getProperty<number>("progress-value");
    if (prevProgress === progress) {
      return;
    }

    object.setProperty("progress-raw", normalizedRaw);
    object.setProperty("progress-value", progress);
  }

  private recomputeProgress(object: StringObject): void {
    const startPosition =
      object.getProperty<number>("start-position") ??
      object.getProperty<number>("progress-start-position") ??
      0;
    const differencePosition =
      object.getProperty<number>("difference-position") ??
      object.getProperty<number>("progress-difference-position") ??
      0;

    object.setProperty("progress-start-position", startPosition);
    object.setProperty("progress-difference-position", differencePosition);

    const rawProgress = this.resolveRawProgress(
      object,
      this.data.scroll.transformedCurrent,
      startPosition,
      differencePosition,
    );

    this.applyRawProgress(object, rawProgress);
  }

  private inactiveDirty: StringObject[] = [];
  private scanDirty = true;
  private scanStart = new Float64Array(0);
  private scanEnd = new Float64Array(0);
  private scanSide = new Int8Array(0);

  private ensureBatchCapacity(length: number): void {
    if (this.batchStarts.length >= length) return;
    this.batchStarts = new Float64Array(length);
    this.batchDiffs = new Float64Array(length);
    this.batchOut = new Float64Array(length);
  }

  onScroll(data: StringData): void {
    super.onScroll(data);
  }

  onObjectConnected(object: StringObject) {
    super.onObjectConnected(object);
  }

  onScrollMeasure(data: StringData) {
    const len = this.objects.length;
    if (len === 0) {
      this.syncInactiveObjects();
      return;
    }

    this.ensureBatchCapacity(len);
    for (let i = 0; i < len; i++) {
      const object = this.objects[i];
      const startPosition =
        object.getProperty<number>("start-position") ??
        object.getProperty<number>("progress-start-position") ??
        0;
      const differencePosition =
        object.getProperty<number>("difference-position") ??
        object.getProperty<number>("progress-difference-position") ??
        0;
      object.setProperty("progress-start-position", startPosition);
      object.setProperty("progress-difference-position", differencePosition);
      this.batchStarts[i] = startPosition;
      this.batchDiffs[i] = differencePosition;
    }

    DEFAULT_PROGRESS_KERNEL.computeRawProgressBatch(
      this.data.scroll.transformedCurrent,
      this.batchStarts,
      this.batchDiffs,
      this.batchOut,
      len,
    );

    for (let i = 0; i < len; i++) {
      const object = this.objects[i];
      const overrideRawProgress = object.getProperty<number | null>(
        STRING_DEV_PROGRESS_OVERRIDE_KEY,
      );
      const rawProgress =
        overrideRawProgress != null
          ? this.sanitizeRawProgress(overrideRawProgress)
          : this.batchOut[i];
      this.applyRawProgress(object, rawProgress);
    }

    this.syncInactiveObjects();
  }

  private syncInactiveObjects(): void {
    const all = this.objectsOnPage;
    const len = all.length;
    if (len === this.objects.length) return;

    if (this.scanDirty) {
      this.scanDirty = false;
      if (this.scanStart.length < len) {
        this.scanStart = new Float64Array(len);
        this.scanEnd = new Float64Array(len);
        this.scanSide = new Int8Array(len);
      }
      for (let i = 0; i < len; i++) {
        const object = all[i];
        const start =
          object.getProperty<number>("start-position") ??
          object.getProperty<number>("progress-start-position") ??
          0;
        const diff =
          object.getProperty<number>("difference-position") ??
          object.getProperty<number>("progress-difference-position") ??
          0;
        this.scanStart[i] = start;
        this.scanEnd[i] = diff > 0 ? start + diff : NaN;
        this.scanSide[i] = SIDE_UNKNOWN;
      }
    }

    const scroll = this.data.scroll.transformedCurrent;
    const starts = this.scanStart;
    const ends = this.scanEnd;
    const sides = this.scanSide;
    for (let i = 0; i < len; i++) {
      const end = ends[i];
      let side = SIDE_INSIDE;
      if (end === end) {
        if (scroll <= starts[i]) side = SIDE_BEFORE;
        else if (scroll >= end) side = SIDE_AFTER;
      }
      if (side === sides[i]) continue;
      sides[i] = side;
      if (side === SIDE_INSIDE) continue;

      const object = all[i];
      if (this.objectMap.has(object.id)) continue;
      const start = starts[i];
      this.applyRawProgress(
        object,
        this.resolveRawProgress(object, scroll, start, end - start),
      );
      if (
        object.getProperty<number>("progress-value") !==
        object.getProperty<number>("progress-applied")
      ) {
        this.inactiveDirty.push(object);
      }
    }
  }

  calculatePositions(object: StringObject, windowSize: number) {
    this.scanDirty = true;
    super.calculatePositions(object, windowSize);
    object.setProperty(
      "progress-start-position",
      object.getProperty<number>("start-position") ??
        object.getProperty<number>("progress-start-position") ??
        0,
    );
    object.setProperty(
      "progress-difference-position",
      object.getProperty<number>("difference-position") ??
        object.getProperty<number>("progress-difference-position") ??
        0,
    );
    this.recomputeProgress(object);
  }

  addObject(id: string, object: StringObject): void {
    this.scanDirty = true;
    super.addObject(id, object);
  }

  removeObject(id: string): void {
    this.scanDirty = true;
    super.removeObject(id);
  }

  onMutate() {
    styleTxn.run(() => {
      const len = this.objects.length;
      for (let i = 0; i < len; i++) {
        this.updateObjectProgress(this.objects[i]);
      }
      const dirty = this.inactiveDirty;
      if (dirty.length > 0) {
        this.inactiveDirty = [];
        for (let i = 0; i < dirty.length; i++) {
          this.updateObjectProgress(dirty[i]);
        }
      }
    });
  }

  private updateObjectProgress(object: StringObject): void {
    const progress = object.getProperty<number>("progress-value") ?? 0;
    const prevProgress = object.getProperty<number>("progress-applied");

    if (prevProgress === progress) {
      return;
    }
    const key = object.getProperty<string>("key");
    const precision = object.getProperty<number>("precision") ?? -1;
    object.setProperty<number>("progress-applied", progress);
    const rawProgress = object.getProperty<number>("progress-raw") ?? progress;
    const parentEasing = object.getProperty<EasingFunctionOutput>("easing");

    const eventName =
      object.getProperty<string>("event-progress-name") ??
      object.getScopedEventName("object:progress");
    object.setProperty("event-progress-name", eventName);
    if (eventName) {
      this.events.emit(eventName, progress);
    }
    this.emitSignal(object, "progress", progress);

    if (key) {
      this.applyVarToElement(object, key, progress);
    }

    // Handle mirrors
    for (let i = 0; i < object.mirrorObjects.length; i++) {
      const mirror = object.mirrorObjects[i];
      const eased = mirror.applyProgress(
        rawProgress,
        typeof parentEasing === "function" ? parentEasing : undefined,
      );
      const easedRounded =
        precision >= 0 ? Math.round(eased * 10 ** precision) / 10 ** precision : eased;
      mirror.setProperty("progress", easedRounded);
      if (key) {
        this.tools.styleTxn.setVar(mirror.htmlElement, key, easedRounded);
      }
    }
  }

  exitObject(id: string): void {
    const object = this.objectMap.get(id);
    if (object) {
      this.recomputeProgress(object);
      styleTxn.run(() => this.updateObjectProgress(object));
    }
    super.exitObject(id);
  }

  onObjectDisconnected(object: StringObject): void {
    super.onObjectDisconnected(object);
    const key = object.getProperty<string>("key");
    if (!key) return;

    const clearCustomProperty = (el: HTMLElement) => {
      el.style.removeProperty(key);
    };

    clearCustomProperty(object.htmlElement);
    const mirrors = object.mirrorObjects;
    for (let i = 0; i < mirrors.length; i++) {
      clearCustomProperty(mirrors[i].htmlElement);
    }
  }
}
